import axios from 'axios';
import {push} from 'react-router-redux';
import uuidV4 from 'uuid/v4';

import customer from '../customer';
const {getActiveSubscriptionId, getUserName} = customer.selectors;

import * as endpoints from '../common/apiEndpoints';
import * as types from './actionTypes';

import {getActivePageSize, getLabels, getPdfId} from './selectors';

export function setGridSorting(isSorting) {
  return {
    type: types.SET_GRID_SORTING,
    payload: {
      isSorting
    }
  };
}

export function setLabelIndex(oldIndex, newIndex) {
  return function(dispatch) {
    dispatch({
      type: types.SET_LABEL_INDEX,
      payload: {
        oldIndex,
        newIndex
      }
    });
    dispatch(prepareComposition());
  };
}

export function selectLabel(index) {
  return {
    type: types.SELECT_LABEL,
    payload: {
      index
    }
  };
}

export function getPetLabelList() {
  return function(dispatch, getStore) {
    dispatch({
      type: types.GET_LABEL_PET_LIST
    });

    const subscriptionId = getActiveSubscriptionId(getStore());
    axios.get(`${endpoints.LABEL_ALL_PETS}?subscriptionID=${subscriptionId}`)
      .then((response) => {
        // Clear nulls
        response.data.forEach((petLabel) => {
          Object.keys(petLabel).forEach((key) => {
            if(petLabel[key] === null) {
              petLabel[key] = '';
            }
          });
        });
        
        dispatch(petLabelListReceived(response.data));
        dispatch(parsePetTypes(response.data));
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

function petLabelListReceived(petLabels) {
  return function(dispatch) {
    dispatch({
      type: types.PET_LABEL_LIST_RECEIVED,
      payload: {
        petLabels
      }
    });
  };
}

export function petLabelListReload(isValid) {
  return function(dispatch) {
    dispatch({
      type: types.PET_LABEL_LIST_RELOAD,
      payload: {
        isValid
      }
    });
  };
}

export function petLabelPriceUpdate(label, price) {
  return function(dispatch) {
    dispatch({
      type: types.PET_LABEL_PRICE_UPDATE,
      payload: {
        label,
        price
      }
    });
  };
}

function parsePetTypes(petLabelList) {
  return function(dispatch) {
    let petTypeSet = new Set([]);
    petLabelList.forEach((petLabel) => {
      petTypeSet.add(petLabel.PetType);
    });
    
    const petTypeList = [];
    petTypeSet.forEach((petType) => {
      petTypeList.push(petType);
    });

    dispatch({
      type: types.PET_TYPES_ADDED,
      payload: {
        petTypes: petTypeList,
        selectedPetTypes: petTypeList
      }
    });
  };
}

export function toggleSelectedPetType(petType) {
  return function(dispatch) {
    dispatch({
      type: types.TOGGLE_SELECTED_PET_TYPE,
      payload: {
        petType: petType
      }
    });
    dispatch(filterPetLabels()); 
  };
}

function setSearchPhrase(searchPhrase) {
  return {
    type: types.LABELS_SEARCH_PHRASE,
    payload: {
      searchPhrase: searchPhrase
    }
  };
}

export function searchPetLabels(searchPhrase) {
  return function(dispatch) {
    dispatch(setSearchPhrase(searchPhrase));
    dispatch(filterPetLabels());
  };
}

function filterPetLabels() {
  return {
    type: types.FILTER_PET_LABELS
  };
}

export function clearSearchResults() {
  return {
    type: types.CLEAR_SEARCH_RESULTS
  };
}

export function selectPetLabel(petLabelId, articleMappingId) {
  return {
    type: types.SELECT_PET_LABEL,
    payload: {
      petLabelId: petLabelId,
      articleMappingId: articleMappingId
    }
  };
}

export function deselectPetLabel() {
  return {
    type: types.DESELECT_PET_LABEL
  };
}

export function clearActiveLabel() {
  return {
    type: types.CLEAR_ACTIVE_LABEL
  };
}

export function getPetLabel(petLabelId, activePageSize, articleMappingId) {
  return function(dispatch, getStore) {
    dispatch({
      type: types.GET_PET_LABEL
    });

    const subscriptionId = getActiveSubscriptionId(getStore());
    axios.get(`${endpoints.LABEL_PET}?subscriptionID=${subscriptionId}&labelName=${activePageSize}&petID=${petLabelId}&articleMappingEntryID=${articleMappingId}`)
      .then((response) => {
        dispatch({
          type: types.PET_LABEL_RECEIVED,
          payload: {
            petLabel: response.data
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

export function getDefinitions() {
  return function(dispatch, getStore) {
    dispatch({
      type: types.GET_LABEL_DEFINITIONS
    });

    const subscriptionId = getActiveSubscriptionId(getStore());
    axios.get(`${endpoints.LABEL_DEFINITIONS}?subscriptionID=${subscriptionId}`)
      .then((response) => {
        dispatch({
          type: types.LABEL_DEFINITIONS_RECEIVED,
          payload: {
            definitions: response.data
          }
        });
        dispatch(selectLabelSize(response.data[0].LabelName));
        dispatch(getCustomDefinitions(response.data[0].LabelName));
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

export function selectLabelSize(labelSize) {
  return function(dispatch) {
    const pdfId = uuidV4();
    dispatch({
      type: types.SELECT_LABEL_SIZE,
      payload: {
        labelSize,
        pdfId
      }
    });
    dispatch(prepareComposition());
  };
}

export function saveLabelToSheet(activePageSize, activePetLabel, fieldMapping, activePLU, values) {
  return function(dispatch, getStore) {
    let label = JSON.parse(JSON.stringify(activePetLabel));
    label.Line1 = values.line1;
    label.Line2 = values.line2;
    label.Price = values.price;
    label.Barcode = values.barcode;
    label.PLUCodeTitle = values.pluKey;
    
    if(activePLU) {
      label.PLUCode = values.plu;
    } else {
      label.PLUCode = null;
    }

    fieldMapping.forEach((mapping) => {
      let field = label.DetailFields.find((detailField) => {
        return detailField.Key === mapping.name;
      });
      field.OverwrittenKey = values[mapping.key];
      field.OverwrittenValue = values[mapping.value];
    });
    
    const subscriptionId = getActiveSubscriptionId(getStore());
    const articleMappingID = activePetLabel.ArticleMappingEntryID;
    axios.post(`${endpoints.LABEL_CREATE_AS_JPG}?subscriptionID=${subscriptionId}&labelName=${activePageSize}&articleMappingEntryID=${articleMappingID}`, label, {responseType: 'blob'})
      .then((response) => {
        let urlCreator = window.URL || window.webkitURL;
        let imageUrl = urlCreator.createObjectURL(response.data);
        dispatch({
          type: types.SAVE_LABEL_TO_SHEET,
          payload: {
            imageUrl,
            label
          }
        });
        
        if(values.price)
          dispatch(petLabelPriceUpdate(activePetLabel, values.price));

        dispatch(clearSearchResults());
        dispatch(deselectPetLabel());
        dispatch(clearActiveLabel());
        dispatch(prepareComposition());
        dispatch(push('/labels'));
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

export function quickSaveLabelToSheet(activePageSize, activePetLabel, price, activePLU)
{
  return function(dispatch, getStore) {
    let label = JSON.parse(JSON.stringify(activePetLabel));
    label.Price = price;

    if(activePLU) {
      label.PLUCode = activePetLabel.PLUCode;
    } else {
      label.PLUCode = null;
    }

    const subscriptionId = getActiveSubscriptionId(getStore());
    const articleMappingID = activePetLabel.ArticleMappingEntryID;

    axios.get(`${endpoints.LABEL_PET}?subscriptionID=${subscriptionId}&labelName=${activePageSize}&petID=${activePetLabel.Id}&articleMappingEntryID=${activePetLabel.ArticleMappingEntryID}`)
      .then((response) => {
        label.DetailFields = response.data.DetailFields;
        //
        label.Line1 = response.data.Line1;
        label.Line2 = response.data.Line2;
        label.PLUCodeTitle = response.data.PLUCodeTitle;
        //
        axios.post(`${endpoints.LABEL_CREATE_AS_JPG}?subscriptionID=${subscriptionId}&labelName=${activePageSize}&articleMappingEntryID=${articleMappingID}`, label, {responseType: 'blob'})
        .then((response) => {
          let urlCreator = window.URL || window.webkitURL;
          let imageUrl = urlCreator.createObjectURL(response.data);
          
          dispatch({
            type: types.SAVE_LABEL_TO_SHEET,
            payload: {
              imageUrl,
              label
            }
          });

          if(price)
            dispatch(petLabelPriceUpdate(activePetLabel, price));
          
          dispatch(prepareComposition());
          dispatch({
            type: types.SHOW_LABEL_MODAL
          });

          dispatch({
            type: types.SELECT_NEXT_LABEL
          });
        })
        .catch(() => {
          dispatch({type: types.SHOW_ERROR_MODAL});
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

export function hideLabelModal() {
  return {
    type: types.HIDE_LABEL_MODAL
  };
}

export function clearLabelSheet() {
  return function(dispatch) {
    dispatch({type: types.CLEAR_LABEL_SHEET});
    dispatch(prepareComposition());
  };
}

export function togglePLU() {
  return function(dispatch) {
    dispatch({type: types.TOGGLE_PLU_ACTIVE});
    dispatch(clearLabelSheet());
  };
}

export function deleteLabel(gridIndex) {
  return function(dispatch) {
    dispatch({
      type: types.DELETE_LABEL_FROM_SHEET,
      payload: {
        gridIndex
      }
    });
    dispatch(prepareComposition());
  };
}

export function editLabel(gridIndex) {
  return function(dispatch) {
    dispatch({
      type: types.EDIT_EXISTING_LABEL,
      payload: {
        gridIndex
      }
    });
    dispatch(push('/edit_label'));
  };
}

export function getCustomDefinitions(activePageSize) {
  return function(dispatch, getStore) {
    dispatch({
      type: types.GET_CUSTOM_DEFINITIONS
    });

    const subscriptionId = getActiveSubscriptionId(getStore());
    axios.get(`${endpoints.LABEL_CUSTOM_DEFINITIONS}?subscriptionID=${subscriptionId}&labelName=${activePageSize}`)
      .then((response) => {
        dispatch({
          type: types.CUSTOM_DEFINITIONS_RECEIVED,
          payload: {
            definitions: response.data
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

export function selectCustomLabel(petType) {
  return {
    type: types.SELECT_CUSTOM_LABEL,
    payload: {
      petType
    }
  };
}

function prepareComposition() {
  return function(dispatch, getStore) {
    let labelSheet = [];
    const storeLabels = getLabels(getStore());
    storeLabels.forEach((label, index) => {
      if(label.hasOwnProperty('data')) {
        let finalLabel = {
          Key: index,
          Value: label.data
        };
        labelSheet.push(finalLabel);
      }
    });
    
    const subscriptionId = getActiveSubscriptionId(getStore());
    const activePageSize = getActivePageSize(getStore());
    const pdfId = getPdfId(getStore());

    axios.post(`${endpoints.LABEL_PREPARE_COMPOSITION}?subscriptionID=${subscriptionId}&labelName=${activePageSize}&urlkey=${pdfId}`, labelSheet)
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

export function getArticleList() {
  return function(dispatch, getStore) {
    dispatch({
      type: types.GET_ARTICLE_LIST
    });

    const subscriptionId = getActiveSubscriptionId(getStore());
    axios.get(`${endpoints.LABEL_ALL_PETS}?subscriptionID=${subscriptionId}&excludeInternalBarcodes=true`)
      .then((response) => {
        // Clear nulls
        response.data.forEach((petLabel) => {
          Object.keys(petLabel).forEach((key) => {
            if(petLabel[key] === null) {
              petLabel[key] = '';
            }
          });
        });
        
        dispatch(articleListReceived(response.data));
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

function articleListReceived(petLabels) {
  return {
    type: types.ARTICLE_LIST_RECEIVED,
    payload: {
      petLabels
    }
  };
}

export function updateLabelArticle(petLabel, key, value) {
  return function(dispatch, getStore) {
    let label = JSON.parse(JSON.stringify(petLabel));
    label[key] = value;
    
    const subscriptionId = getActiveSubscriptionId(getStore());
    let updatedMapping = {
      SubscriptionId: subscriptionId,
      ArticleMappingEntryID: label.ArticleMappingEntryID,
      PetId: label.Id,
      CustomerName: label.CustomerName,
      Price: label.Price,
      PLUCode: label.PLUCode,
      Barcode: label.Barcode
    };
        
    axios.post(`${endpoints.ARTICLE_UPDATE_MAPPING}`, updatedMapping)
      .then((response) => {
        let responseLabel = JSON.parse(JSON.stringify(response.data));
        Object.keys(responseLabel).forEach((key) => {
          if(responseLabel[key] === null) {
            responseLabel[key] = '';
          }
        });

        dispatch({
          type: types.UPDATE_LABEL_ARTICLE,
          payload: {
            label: responseLabel
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });

      // This is to keep the value from flashing before we receive the server response.
      dispatch({
        type: types.UPDATE_LABEL_ARTICLE,
        payload: {
          label
        }
      });
  };
}

export function sortArticles(sortColumn, sortDirection) {
  return {
    type: types.SORT_ARTICLE_COLUMN,
    payload: {
      sortColumn,
      sortDirection
    }
  };
}

export function clearArticleFilters() {
  return {
    type: types.CLEAR_ARTICLE_FILTERS
  };
}

export function addArticleFilter(column, value) {
  return {
    type: types.ADD_ARTICLE_FILTER,
    payload: {
      column: column,
      value: value
    }
  };
}

export function addArticleMapping(baseArticle) {
  return function(dispatch, getStore) {
    const subscriptionId = getActiveSubscriptionId(getStore());
    let updatedMapping = {
      SubscriptionId: subscriptionId,
      PetId: baseArticle.Id,
      CustomerName: baseArticle.CustomerName,
      Price: baseArticle.Price,
      PLUCode: baseArticle.PLUCode,
      Barcode: baseArticle.Barcode
    };

    axios.post(`${endpoints.ARTICLE_ADD_MAPPING}`, updatedMapping)
      .then((response) => {
        
        let responseLabel = JSON.parse(JSON.stringify(response.data));
        Object.keys(responseLabel).forEach((key) => {
          if(responseLabel[key] === null) {
            responseLabel[key] = '';
          }
        });

        dispatch({
          type: types.ADD_ARTICLE_MAPPING,
          payload: {
            article: responseLabel
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });   
  };
}

export function deleteArticleMapping(article) {
  return function(dispatch, getStore) {
    const subscriptionId = getActiveSubscriptionId(getStore());
    let removeMapping = {
      SubscriptionId: subscriptionId,
      ArticleMappingEntryID: article.ArticleMappingEntryID,
      PetId: article.Id
    };

    axios.post(`${endpoints.ARTICLE_DELETE_MAPPING}`, removeMapping)
      .then(() => {
        dispatch({
          type: types.DELETE_ARTICLE_MAPPING,
          payload: {
            article
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });  
  };
}

export function uploadExcelFile(file) {
  return function(dispatch, getStore) {
    dispatch({
      type: types.UPLOAD_EXCEL_MAPPINGS
    });

    let fd = new FormData();
    fd.append('filename', file);
    const config = {headers: {'Content-Type': 'multipart/form-data'}};

    const subscriptionId = getActiveSubscriptionId(getStore());
    axios.post(`${endpoints.ARTICLE_EXCEL_UPLOAD}?subscriptionID=${subscriptionId}`, fd, config)
      .then(() => {
        dispatch({
          type: types.UPLOAD_MAPPINGS_SUCCESS
        });

        dispatch(getArticleList());
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

export function saveLabelStyles(activeDefinition, styleElements) {
  return function(dispatch, getStore) {
    let definitionElements = JSON.parse(JSON.stringify(activeDefinition.StyleElements));
    for(let index = 0; index < definitionElements.length; index++)
    {
      switch(definitionElements[index].Name)
      {
        case 'labelborder':
        {
          definitionElements[index].ColorCode = styleElements.labelBorderColor;
          break;
        }
        case 'background':
        {
          definitionElements[index].ColorCode = styleElements.labelBgColor;
          break;
        }
        case 'headerbackground':
        {
          definitionElements[index].ColorCode = styleElements.headerBgColor;
          break;
        }
        case 'headercolor':
        {
          definitionElements[index].ColorCode = styleElements.headerFontColor;
          break;
        }
        case 'detailtitlebackground':
        {
          definitionElements[index].ColorCode = styleElements.detailBgColor;
          break;
        }
        case 'detailtitlecolor':
        {
          definitionElements[index].ColorCode = styleElements.detailFontColor;
          break;
        }
        case 'detailvaluebackground':
        {
          definitionElements[index].ColorCode = styleElements.valueBgColor;
          break;
        }
        case 'detailvaluecolor':
        {
          definitionElements[index].ColorCode = styleElements.valueFontColor;
          break;
        }
        case 'pricecolor':
        {
          definitionElements[index].ColorCode = styleElements.priceFontColor;
          break;
        }
        case 'pricebackground':
        {
          definitionElements[index].ColorCode = styleElements.priceBgColor;
          break;
        }
        case 'pluprice':
        {
          definitionElements[index].ColorCode = styleElements.pluFontColor;
          break;
        }
        case 'barcodebackground':
        {
          definitionElements[index].ColorCode = styleElements.barCodeBgColor;
          break;
        }
        case 'imageborder':
        {
          definitionElements[index].ColorCode = styleElements.imageBorderColor;
          break;
        }
        case 'rightbackground':
        {
          definitionElements[index].ColorCode = styleElements.rightPanelBgColor;
          break;
        }
      }
    }

    let styles = {
      'Styles': {}
    };
    styles.Styles[activeDefinition.LabelName] = definitionElements;
    
    const subscriptionId = getActiveSubscriptionId(getStore());
    axios.post(`${endpoints.CUSTOMIZE_LABEL_SETTINGS}?subscriptionID=${subscriptionId}`, styles)
      .then((response) => {
        dispatch({
          type: types.UPDATE_LABEL_STYLES,
          payload:
          {
            LabelStyles: response.data.LabelSettings.Styles
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });

  };
}

export function uploadLogo(activePageSize, file) {
  return function(dispatch, getStore) {
    const subscriptionId = getActiveSubscriptionId(getStore());
    
    let fd = new FormData();
    fd.append('filename', file);
    const config = {headers: {'Content-Type': 'multipart/form-data'}};

    axios.post(`${endpoints.UPLOAD_LABEL_LOGO}?subscriptionID=${subscriptionId}&labelName=${activePageSize}`, fd, config)
      .then((response) => {
        dispatch({
          type: types.UPDATE_LABEL_LOGOS,
          payload:
          {
            LabelLogos: response.data.Logos
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });
  };
}

export function savePageSetup(activePageSize, verticalOffset, horizontalOffset)
{
  return function(dispatch, getStore) {
    const subscriptionId = getActiveSubscriptionId(getStore());
    let pageOffsets = {
      'PageRenderingPositionOffset': {
        'PosX': horizontalOffset,
        'PosY': verticalOffset
      }
    };
        
    axios.post(`${endpoints.CUSTOMIZE_LABEL_SETTINGS}?subscriptionID=${subscriptionId}`, pageOffsets)
      .then((response) => {
        dispatch({
          type: types.UPDATE_LABEL_OFFSET,
          payload: {
            pageOffset: response.data.LabelSettings.PageRenderingPositionOffset
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });

  };
}

export function createCalibrationLink() {
  return function(dispatch, getStore)
  {
    const subscriptionId = getActiveSubscriptionId(getStore());
    const userName = getUserName(getStore());
    const pdfLink = `${endpoints.CALIBRATION_GET_PDF}?subscriptionID=${subscriptionId}&userid=${userName}`;

    dispatch({
      type: types.CALIBRATION_PDF_LINK,
      payload: {
        link: pdfLink
      }
    });
  };
}

export function disableLabelAdd() {
  return {
    type: types.DISABLE_LABEL_ADD
  };
}

export function enableLabelAdd() {
  return {
    type: types.ENABLE_LABEL_ADD
  };
}

export function saveLabelSettings(isPaperSizeLetter,usePLUCode ,useUSMetrics, labelTitleOrder  , verticalOffset, horizontalOffset)
{
  return function(dispatch, getStore) {
    const subscriptionId = getActiveSubscriptionId(getStore());
    let updateData = {
      'PageRenderingPositionOffset': {
        'PosX': horizontalOffset,
        'PosY': verticalOffset
      },
      'IsPaperSizeLetter': isPaperSizeLetter,
					'UsePLUCode': usePLUCode,
					'UseUSMetrics': useUSMetrics,
					'LabelTitleOrder': labelTitleOrder
    };
        
    axios.post(`${endpoints.CUSTOMIZE_LABEL_SETTINGS}?subscriptionID=${subscriptionId}`, updateData)
      .then((response) => {
        dispatch({
          type: types.UPDATE_LABEL_OFFSET,
          payload: {
            pageOffset: response.data.LabelSettings.PageRenderingPositionOffset,

            isPaperSizeLetter: response.data.LabelSettings.IsPaperSizeLetter,

            usePLUCode: response.data.LabelSettings.UsePLUCode,

            useUSMetrics: response.data.LabelSettings.UseUSMetrics,

            labelTitleOrder: response.data.LabelSettings.LabelTitleOrder,
          
          }
        });
      })
      .catch(() => {
        dispatch({type: types.SHOW_ERROR_MODAL});
      });

  };
}


/** WEBPACK FOOTER **
 ** ./src/modules/labels/actions.js
 **/